// Filtro periodo (#7, richiesta Davide 21/07: «filtro data con calendarietto
// per scegliere il periodo»). Dal 06/08 la data si SCEGLIE — ordine (default),
// fatturazione o documento: la regola vera sta in `repository._giorno_periodo`.
//
// ponytail: `<input type="date">` nativo — il calendario è quello del sistema
// operativo, già localizzato in italiano, accessibile da tastiera e senza una
// riga di JS. Nessuna libreria di date-picker: sarebbero ~40 KB per rifare
// peggio quello che il browser fa da solo.
//
// Le scorciatoie non sono decorazione: la fatturazione si guarda quasi sempre
// per mese (è il ritmo della liquidazione IVA), e digitare due date per
// un'operazione che si fa ogni giorno è attrito puro.

import type { CampoData } from "../types";

export interface DateRange {
  from: string; // ISO YYYY-MM-DD, "" = nessun limite
  to: string;
}

export const RANGE_VUOTO: DateRange = { from: "", to: "" };

// Il selettore prende il posto dell'etichetta invece di aggiungersi accanto:
// così la riga dei filtri non cresce e, soprattutto, non può mai esistere una
// scritta che dice una data mentre il filtro ne guarda un'altra.
export const CAMPI_DATA: Array<{ valore: CampoData; label: string }> = [
  { valore: "order", label: "Data ordine" },
  { valore: "invoice", label: "Data fatt." },
  { valore: "document", label: "Data doc" },
];

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Scorciatoie calcolate al click, non al render: una pagina lasciata aperta a
 *  cavallo di mezzanotte non deve applicare un periodo di ieri. */
const SCORCIATOIE: Array<{ label: string; calcola: () => DateRange }> = [
  {
    label: "Questo mese",
    calcola: () => {
      const oggi = new Date();
      return { from: iso(new Date(oggi.getFullYear(), oggi.getMonth(), 1)), to: iso(oggi) };
    },
  },
  {
    label: "Mese scorso",
    calcola: () => {
      const oggi = new Date();
      return {
        from: iso(new Date(oggi.getFullYear(), oggi.getMonth() - 1, 1)),
        to: iso(new Date(oggi.getFullYear(), oggi.getMonth(), 0)),
      };
    },
  },
  {
    label: "Quest'anno",
    calcola: () => {
      const oggi = new Date();
      return { from: iso(new Date(oggi.getFullYear(), 0, 1)), to: iso(oggi) };
    },
  },
];

const INPUT_CLS =
  "h-[34px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-medium text-ink outline-none transition-colors hover:border-ink-4 focus:border-ink-3 focus:ring-[3px] focus:ring-ink/5 max-md:h-11 max-md:rounded-xl max-md:px-3 max-md:text-[15px] max-md:text-ink";

export function DateRangeFilter({
  value,
  onChange,
  campo,
  onCampoChange,
}: {
  value: DateRange;
  onChange: (next: DateRange) => void;
  campo: CampoData;
  onCampoChange: (next: CampoData) => void;
}) {
  const attivo = Boolean(value.from || value.to);
  const etichetta = CAMPI_DATA.find((c) => c.valore === campo)?.label ?? "Data";
  // Vincolo incrociato sugli input nativi: il browser impedisce da solo di
  // scegliere un "dal" successivo al "al" (niente validazione a mano, niente
  // messaggio d'errore da scrivere).
  return (
    <div className="flex flex-wrap items-center gap-1.5 max-md:gap-2">
      <select
        value={campo}
        onChange={(e) => onCampoChange(e.target.value as CampoData)}
        aria-label="Su quale data filtrare il periodo"
        title="Su quale data filtrare il periodo"
        className={`${INPUT_CLS} cursor-pointer max-md:basis-full`}
      >
        {CAMPI_DATA.map((c) => (
          <option key={c.valore} value={c.valore}>
            {c.label}
          </option>
        ))}
      </select>
      <input
        type="date"
        value={value.from}
        max={value.to || undefined}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        aria-label={`${etichetta}: dal`}
        className={`${INPUT_CLS} max-md:min-w-[40%] max-md:flex-1`}
      />
      <span className="text-[12px] text-ink-2 max-md:text-ink">→</span>
      <input
        type="date"
        value={value.to}
        min={value.from || undefined}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        aria-label={`${etichetta}: al`}
        className={`${INPUT_CLS} max-md:min-w-[40%] max-md:flex-1`}
      />
      {SCORCIATOIE.map((s) => (
        <button
          key={s.label}
          type="button"
          onClick={() => onChange(s.calcola())}
          className="h-[30px] rounded-full px-2.5 text-[12.5px] font-semibold text-ink underline-offset-4 transition-colors duration-[120ms] hover:bg-paper-2 hover:underline max-md:no-underline max-md:h-10 max-md:border max-md:border-line-2 max-md:rounded-full max-md:bg-card max-md:px-4 max-md:text-[14px] max-md:font-medium max-md:text-ink"
        >
          {s.label}
        </button>
      ))}
      {attivo && (
        <button
          type="button"
          onClick={() => onChange(RANGE_VUOTO)}
          title="Rimuovi il filtro periodo"
          className="h-[30px] rounded-full px-2 text-[12.5px] font-semibold text-ink underline underline-offset-4 hover:bg-paper-2 max-md:h-10 max-md:px-3 max-md:text-[14px] max-md:text-ink max-md:underline"
        >
          Azzera
        </button>
      )}
    </div>
  );
}
